import { createSlice } from "@reduxjs/toolkit";

import {
  addLocationGrade,
  fetchLocationGrades,
  removeLocationGrade,
} from "../thunks/locationGradesThunks";

export interface LocationGrade {
  location_grade_id: string;
  location_id: string;
  grade_id: string;
  grade?: {
    grade_id: string;
    name: string;
    alias_1: string | null;
    alias_2: string | null;
    alias_3: string | null;
    required_vapor: boolean;
  };
}

interface LocationGradesState {
  /** Map of locationId → grades for that location */
  gradesByLocation: Record<string, LocationGrade[]>;
  /** Which locationId is currently being fetched */
  loadingLocationId: string | null;
  /** locationId → in-flight add/remove operation */
  savingByLocation: Record<string, boolean>;
  error: string | null;
}

const initialState: LocationGradesState = {
  gradesByLocation: {},
  loadingLocationId: null,
  savingByLocation: {},
  error: null,
};

export const locationGradesSlice = createSlice({
  name: "locationGrades",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ── Fetch ──────────────────────────────────────────────────────────────
    builder
      .addCase(fetchLocationGrades.pending, (state, action) => {
        state.loadingLocationId = action.meta.arg.locationId;
        state.error = null;
      })
      .addCase(fetchLocationGrades.fulfilled, (state, action) => {
        const { locationId } = action.meta.arg;
        state.gradesByLocation[locationId] = action.payload ?? [];
        state.loadingLocationId = null;
      })
      .addCase(fetchLocationGrades.rejected, (state, action) => {
        state.loadingLocationId = null;
        state.error = action.payload as string;
      });

    // ── Add ────────────────────────────────────────────────────────────────
    builder
      .addCase(addLocationGrade.pending, (state, action) => {
        state.savingByLocation[action.meta.arg.locationId] = true;
        state.error = null;
      })
      .addCase(addLocationGrade.fulfilled, (state, action) => {
        const { locationId } = action.meta.arg;
        state.savingByLocation[locationId] = false;
        const existing = state.gradesByLocation[locationId] ?? [];
        state.gradesByLocation[locationId] = [...existing, action.payload];
      })
      .addCase(addLocationGrade.rejected, (state, action) => {
        state.savingByLocation[action.meta.arg.locationId] = false;
        state.error = action.payload as string;
      });

    // ── Remove ─────────────────────────────────────────────────────────────
    builder
      .addCase(removeLocationGrade.pending, (state, action) => {
        state.savingByLocation[action.meta.arg.locationId] = true;
        state.error = null;
      })
      .addCase(removeLocationGrade.fulfilled, (state, action) => {
        const { locationId, locationGradeId } = action.meta.arg;
        state.savingByLocation[locationId] = false;
        const existing = state.gradesByLocation[locationId] ?? [];
        state.gradesByLocation[locationId] = existing.filter(
          (lg) => lg.location_grade_id !== locationGradeId
        );
      })
      .addCase(removeLocationGrade.rejected, (state, action) => {
        state.savingByLocation[action.meta.arg.locationId] = false;
        state.error = action.payload as string;
      });
  },
});

export default locationGradesSlice.reducer;
